"use strict";
// Copyright (c) Jupyter Development Team.
// Distributed under the terms of the Modified BSD License.
Object.defineProperty(exports, "__esModule", { value: true });
var signaling_1 = require("@phosphor/signaling");
/**
 * A concrete implementation of IObservbleMap<T>.
 */
var ObservableMap = /** @class */ (function () {
    /**
     * Construct a new observable map.
     */
    function ObservableMap(options) {
        if (options === void 0) { options = {}; }
        this._map = new Map();
        this._changed = new signaling_1.Signal(this);
        this._isDisposed = false;
        this._itemCmp = options.itemCmp || Private.itemCmp;
        if (options.values) {
            for (var key in options.values) {
                this._map.set(key, options.values[key]);
            }
        }
    }
    Object.defineProperty(ObservableMap.prototype, "type", {
        /**
         * The type of the Observable.
         */
        get: function () {
            return 'Map';
        },
        enumerable: true,
        configurable: true
    });
    Object.defineProperty(ObservableMap.prototype, "changed", {
        /**
         * A signal emitted when the map has changed.
         */
        get: function () {
            return this._changed;
        },
        enumerable: true,
        configurable: true
    });
    Object.defineProperty(ObservableMap.prototype, "isDisposed", {
        /**
         * Whether this map has been disposed.
         */
        get: function () {
            return this._isDisposed;
        },
        enumerable: true,
        configurable: true
    });
    Object.defineProperty(ObservableMap.prototype, "size", {
        /**
         * The number of key-value pairs in the map.
         */
        get: function () {
            return this._map.size;
        },
        enumerable: true,
        configurable: true
    });
    /**
     * Set a key-value pair in the map
     *
     * @param key - The key to set.
     *
     * @param value - The value for the key.
     *
     * @returns the old value for the key, or undefined
     *   if that did not exist.
     *
     * @throws if the new value is undefined.
     *
     * #### Notes
     * This is a no-op if the value does not change.
     */
    ObservableMap.prototype.set = function (key, value) {
        var oldVal = this._map.get(key);
        if (value === undefined) {
            throw Error('Cannot set an undefined value, use remove');
        }
        // Bail if the value does not change.
        var itemCmp = this._itemCmp;
        if (oldVal !== undefined && itemCmp(oldVal, value)) {
            return oldVal;
        }
        this._map.set(key, value);
        this._changed.emit({
            type: oldVal ? 'change' : 'add',
            key: key,
            oldValue: oldVal,
            newValue: value
        });
        return oldVal;
    };
    /**
     * Get a value for a given key.
     *
     * @param key - the key.
     *
     * @returns the value for that key.
     */
    ObservableMap.prototype.get = function (key) {
        return this._map.get(key);
    };
    /**
     * Check whether the map has a key.
     *
     * @param key - the key to check.
     *
     * @returns `true` if the map has the key, `false` otherwise.
     */
    ObservableMap.prototype.has = function (key) {
        return this._map.has(key);
    };
    /**
     * Get a list of the keys in the map.
     *
     * @returns - a list of keys.
     */
    ObservableMap.prototype.keys = function () {
        var keyList = [];
        this._map.forEach(function (v, k) {
            keyList.push(k);
        });
        return keyList;
    };
    /**
     * Get a list of the values in the map.
     *
     * @returns - a list of values.
     */
    ObservableMap.prototype.values = function () {
        var valList = [];
        this._map.forEach(function (v, k) {
            valList.push(v);
        });
        return valList;
    };
    /**
     * Remove a key from the map
     *
     * @param key - the key to remove.
     *
     * @returns the value of the given key,
     *   or undefined if that does not exist.
     */
    ObservableMap.prototype.delete = function (key) {
        var oldVal = this._map.get(key);
        this._map.delete(key);
        this._changed.emit({
            type: 'remove',
            key: key,
            oldValue: oldVal,
            newValue: undefined
        });
        return oldVal;
    };
    /**
     * Set the ObservableMap to an empty map.
     */
    ObservableMap.prototype.clear = function () {
        // Delete one by one to emit the correct signals.
        var keyList = this.keys();
        for (var i = 0; i < keyList.length; i++) {
            this.delete(keyList[i]);
        }
    };
    /**
     * Dispose of the resources held by the map.
     */
    ObservableMap.prototype.dispose = function () {
        if (this.isDisposed) {
            return;
        }
        this._isDisposed = true;
        signaling_1.Signal.clearData(this);
        this._map.clear();
    };
    return ObservableMap;
}());
exports.ObservableMap = ObservableMap;
/**
 * The namespace for module private data.
 */
var Private;
(function (Private) {
    /**
     * The default strict equality item comparator.
     */
    function itemCmp(first, second) {
        return first === second;
    }
    Private.itemCmp = itemCmp;
})(Private || (Private = {}));
